import type {Changes} from "./Changes";
import {getIdAsKey, hydrateField, INVALIDATE} from "./mate-shared";
import {
  affectedLists,
  fieldName,
  setRowField,
  targetKeys,
  type CachedLists,
  type FieldsAffectingRelations,
  type KeysByParent,
  type ListTargets,
  type ParentRef,
  type TargetingContext,
} from "./list-targets";
import {rowMatchesQuery} from "./relation-filter";
import type {
  CacheEntry as Entry,
  FieldDependents,
  ModelCache,
  ModelDesc,
  ModelName,
  MutationDesc,
} from "./mate-types";

/** What invalidation reads that lives as long as the cache. */
export type InvalidationContext = TargetingContext & {
  modelCache: ModelCache;
  /** lists of another model a field reaches through a relation, e.g. `account.cardOrders({card: {deckId}})` */
  fieldsAffectingOtherRelations: FieldsAffectingRelations;
  /** lists of another model whose query reaches this model through a has-many relation, e.g. `account.cards({handCards: {userId}})` under handCard */
  relsAffectingRelations: Partial<Record<ModelName, KeysByParent>>;
  fieldDependents: FieldDependents;
  /** the relations pointing at each model's rows: {milestone: [{model: "card", relName: "milestone", fk: "milestoneId"}]} */
  belongsToModelDeps: Record<ModelName, ParentRef[]>;
  markInvalid: (entry: Entry, field: string) => void;
};

type InvalidateParams = {
  ctx: InvalidationContext;
  desc: MutationDesc & {type: "update" | "delete" | "create"};
  data: Record<string, any>;
  changes: Changes;
  lists: CachedLists;
};

/** The row id `data` names through the model's id props, or through `id` on a single-id model. */
const rowIdOf = (modelDesc: ModelDesc, data: Record<string, any>): string | null => {
  const idParts = modelDesc.idPropAsArray.map((prop) => data[prop]);
  if (idParts.every((val) => val !== undefined)) return getIdAsKey(idParts);
  return data.id && modelDesc.idPropAsArray.length === 1 ? data.id : null;
};

/** A row as a write leaves it; a field written as `INVALIDATE` becomes unknown. */
const applyWrite = (
  modelDesc: ModelDesc,
  row: Record<string, unknown>,
  fields: Record<string, unknown>
) => {
  const result = {...row};
  for (const [field, val] of Object.entries(fields)) setRowField(modelDesc, result, field, val);
  return result;
};

/** Marks the relation keys stale on every cached parent that holds them, loaded or loading. */
const invalidateOnEveryParent = ({ctx, changes}: InvalidateParams, keysByParent: KeysByParent) => {
  for (const [parentModel, keys] of Object.entries(keysByParent) as [ModelName, Set<string>][]) {
    const parentEntries = ctx.modelCache[parentModel];
    if (!parentEntries) continue;
    for (const [parentId, entry] of Object.entries(parentEntries)) {
      if (!entry) continue;
      for (const key of keys) {
        if (key in entry.value || key in entry.status) {
          ctx.markInvalid(entry, key);
          // it's a change because $isLoaded now returns a different value and the model's cache isn't valid anymore
          changes.add(parentModel, parentId);
        }
      }
    }
  }
};

/** The keys of other models' lists whose query reads one of `fields` of `model`, per parent model. */
const otherListKeys = (ctx: InvalidationContext, model: ModelName, fields: Iterable<string>) => {
  const keysByParent: KeysByParent = {};
  const byField = ctx.fieldsAffectingOtherRelations[model];
  if (!byField) return keysByParent;
  for (const field of fields) {
    for (const [parentModel, keys] of Object.entries(byField[field] ?? {}) as [
      ModelName,
      Set<string>,
    ][]) {
      const merged = (keysByParent[parentModel] ??= new Set());
      for (const key of keys) merged.add(key);
    }
  }
  return keysByParent;
};

/** The cached row's fields, and which of them are marked for reloading. */
const cachedRow = (entry: Entry | null | undefined) => ({
  before: entry ? entry.value : {},
  isStale: entry ? (field: string) => field in entry.status : undefined,
});

/**
 * Marks every cached list of the row's parents stale that the write can change. `anyParentDone`
 * spans the rows of one write: `anyParent` reaches the same keys for each of them.
 *
 * `named` holds only the fields a create or delete names, which are fresh where the cached row may
 * not be; a key whose query or relation filter it rules out keeps its rows. It is checked without
 * a match context: a cached parent row may be stale, and a wrong `false` would skip a reload.
 */
const invalidateLists = (
  bag: InvalidateParams,
  id: string | null,
  written: ReadonlySet<string>,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  isStale?: (field: string) => boolean,
  anyParentDone = new Set<string>(),
  named: Record<string, unknown> | null = null
) => {
  const {ctx, desc, lists, changes} = bag;
  const modelDesc = ctx.descriptions[desc.model];
  const ruledOut = new Map<string, boolean>();
  const isRuledOut = (query: Record<string, unknown> | null, memoKey: string) => {
    if (!named || !query) return false;
    let res = ruledOut.get(memoKey);
    if (res === undefined) {
      res = rowMatchesQuery(query, modelDesc, named) === false;
      ruledOut.set(memoKey, res);
    }
    return res;
  };
  const markKeys = (target: ListTargets, parentId: string, countsOnly: boolean) => {
    const {parent} = target;
    const keys = targetKeys(lists, target, parentId, countsOnly).filter(
      (info) => !isRuledOut(info.matchQuery, `${parent.model}:${info.key}`)
    );
    if (!keys.length) return;
    const entry =
      parent.model === "_root"
        ? (ctx.modelCache._root as unknown as Entry | undefined)
        : ctx.modelCache[parent.model]?.[parentId];
    if (!entry) return;
    for (const {key} of keys) ctx.markInvalid(entry, key);
    changes.add(parent.model, parent.model === "_root" ? null : parentId);
  };
  for (const target of affectedLists(ctx, lists, desc.model, id, written, before, after, isStale)) {
    const {parent} = target;
    if (isRuledOut(target.relDesc.filter ?? null, `${parent.model}.${parent.relName}`)) continue;
    for (const parentId of target.parents.keys()) markKeys(target, parentId, false);
    if (!target.anyParent) continue;
    const doneKey = `${parent.model}:${parent.relName}:${target.anyParentCountsOnly}`;
    if (anyParentDone.has(doneKey)) continue;
    anyParentDone.add(doneKey);
    const parentIds = lists.parentIdsOf(parent);
    if ((!before || !after) && process.env.NODE_ENV !== "production") {
      console.info(
        `can't tell the ${parent.model} of a ${before ? "deleted" : "created"} ${desc.model}, therefore invalidating ${parentIds.length} ${parent.model} for ${parent.relName}`
      );
    }
    for (const parentId of parentIds) {
      if (!target.parents.has(parentId)) markKeys(target, parentId, target.anyParentCountsOnly);
    }
  }
};

export const invalidateAfterUpdate = (bag: InvalidateParams) => {
  const {ctx, desc, data} = bag;
  const {model: modelName} = desc;
  const modelDesc = ctx.descriptions[modelName];
  const entryId = rowIdOf(modelDesc, data);
  const {ids: dataIds, ...changedFields} = data;
  for (const prop of modelDesc.idPropAsArray) delete changedFields[prop];
  // `undefined` comes from an implicit update built without its retVal; it's unknown, not a value
  for (const [field, val] of Object.entries(changedFields)) {
    if (val === undefined) changedFields[field] = INVALIDATE;
  }
  if (entryId !== null && entryId === data.id) delete changedFields.id;

  const modelEntries = ctx.modelCache[modelName] || {};

  if (process.env.NODE_ENV !== "production" && !entryId && !dataIds) {
    console.info(
      `updating all ${
        Object.keys(modelEntries).length
      } instances of ${modelName} since no id was given`
    );
  }
  const ids: string[] = entryId ? [entryId] : dataIds || Object.keys(modelEntries);
  if (!ids.length) return;

  const fields = Object.entries(changedFields).map(([field, val]) => ({
    field,
    name: fieldName(modelDesc, field),
    val,
    dependents: ctx.fieldDependents[modelName]?.[field] ?? [],
  }));
  const written = new Set(fields.map((f) => f.name));
  const anyParentDone = new Set<string>();

  for (const id of ids) {
    const cacheEntry = modelEntries[id];
    if (cacheEntry) bag.changes.add(modelName, id);

    const {before, isStale} = cachedRow(cacheEntry);
    const after = applyWrite(modelDesc, before, changedFields);
    invalidateLists(bag, id, written, before, after, isStale, anyParentDone);

    if (!cacheEntry) continue;
    for (const {name, val, dependents} of fields) {
      ctx.markInvalid(cacheEntry, name);
      for (const dep of dependents) {
        ctx.markInvalid(cacheEntry, dep);
        const relName = modelDesc.fkToBelongsTo[dep]?.name;
        if (relName) ctx.markInvalid(cacheEntry, relName);
      }
      if (val !== null && val !== INVALIDATE) {
        cacheEntry.value[name] = hydrateField(modelDesc.fields[name]?.type, val);
      }
    }
  }

  // lists of another model don't depend on which row changed, so one pass covers every id
  invalidateOnEveryParent(
    bag,
    otherListKeys(
      ctx,
      modelName,
      fields.map((f) => f.field)
    )
  );
};

export const invalidateAfterDelete = (bag: InvalidateParams) => {
  const {ctx, desc, changes, data} = bag;
  const modelDesc = ctx.descriptions[desc.model];
  const idKey = rowIdOf(modelDesc, data);
  if (!idKey) {
    console.warn(
      `deleting a resource without passing '${modelDesc.idPropAsArray.join(", ")}'`,
      data,
      desc
    );
    return;
  }
  const cacheEntry = ctx.modelCache[desc.model]?.[idKey];
  if (cacheEntry) {
    changes.add(desc.model, idKey);
    for (const key of Object.keys(cacheEntry.value)) ctx.markInvalid(cacheEntry, key);
  }
  for (const {model: parentModelName, relName, fk} of ctx.belongsToModelDeps[desc.model]) {
    const modelEntries = ctx.modelCache[parentModelName];
    if (!modelEntries) continue;
    for (const [id, parentCacheEntry] of Object.entries(modelEntries)) {
      if (!parentCacheEntry) continue;
      for (const key of [relName, fk]) {
        if (key && parentCacheEntry.value[key] === idKey) {
          ctx.markInvalid(parentCacheEntry, key);
          changes.add(parentModelName, id);
        }
      }
    }
  }
};

/** Marks the lists stale that a created or deleted row joins or leaves. */
export const invalidateListsAfterCreateOrDelete = (bag: InvalidateParams) => {
  const {ctx, desc, data} = bag;
  const modelDesc = ctx.descriptions[desc.model];
  const {ids, ...rowData} = data;
  const getIds = (): (string | null)[] => {
    const id = rowIdOf(modelDesc, data);
    if (id !== null) return [id];
    return Array.isArray(ids) && ids.length ? ids : [null];
  };
  if (desc.type === "create") {
    const after = applyWrite(modelDesc, {}, rowData);
    const written = new Set(Object.keys(after));
    const anyParentDone = new Set<string>();
    for (const id of getIds()) {
      invalidateLists(bag, id, written, null, after, undefined, anyParentDone, after);
    }
  } else {
    // what the delete names of the row is fresh, whatever the cache holds
    const named = applyWrite(modelDesc, {}, rowData);
    const given = new Set(Object.keys(named));
    for (const id of getIds()) {
      const cached = cachedRow(id === null ? null : ctx.modelCache[desc.model]?.[id]);
      const before = applyWrite(modelDesc, cached.before, rowData);
      const {isStale} = cached;
      invalidateLists(
        bag,
        id,
        new Set(Object.keys(before)),
        before,
        null,
        isStale && ((field) => !given.has(field) && isStale(field)),
        undefined,
        named
      );
    }
  }

  // Lists of another model. A new row is only taken in by those that reach it through a has-many
  // relation, e.g. `account.cards({handCards: {userId}})` by a new handCard; nothing points at it
  // yet. A deleted row also changes those reaching it through a belongsTo, e.g.
  // `account.cardOrders({card: {deckId}})` holds the order rows of a deleted card.
  if (desc.type === "create") {
    const keysByParent = ctx.relsAffectingRelations[desc.model];
    if (keysByParent) invalidateOnEveryParent(bag, keysByParent);
  } else {
    const byField = ctx.fieldsAffectingOtherRelations[desc.model];
    if (byField) invalidateOnEveryParent(bag, otherListKeys(ctx, desc.model, Object.keys(byField)));
  }
};
